<template>
  <div class="sales-by-product">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Product</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search item, code, SKU or barcode" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-coin"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Revenue</div>
          <div class="rp-card-value">{{ formatMoney(summary.revenue) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.product_count, 'product') }} · {{ plural(summary.invoice_count, 'invoice') }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-package-export"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Quantity Sold</div>
          <div class="rp-card-value">{{ formatQty(summary.qty_sold) }}</div>
          <div class="rp-card-sub">
            {{ formatQty(summary.returned_qty) }} returned · net {{ formatQty(summary.qty) }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-receipt-tax"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.cost) }}</div>
          <div class="rp-card-sub">batch cost of the units sold, less returns</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Gross Profit</div>
          <div class="rp-card-value" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</div>
          <div class="rp-card-sub">{{ summary.margin }}% margin · returns {{ summary.return_rate }}% of sales</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Top products by revenue</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Revenue</span>
            <span><i class="rp-dot" :style="{ background: PROFIT_COLOR }"></i>Profit</span>
          </span>
        </div>
        <div v-show="summary.top_products.length" class="rp-chart" :style="{ height: `${chartHeight}px` }">
          <canvas ref="topChart"></canvas>
        </div>
        <p v-if="!summary.top_products.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Revenue by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(category.revenue) }}
                <span class="text-muted fw-normal">· profit {{ formatMoney(category.profit) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highlights</div>
        <ul v-if="highlights.length" class="rp-highlights">
          <li v-for="item in highlights" :key="item.label" :class="item.tone">
            <span class="hl-icon"><i class="ti" :class="item.icon"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">{{ item.label }}</small>
              <div class="fw-semibold text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ item.detail }}</small>
            </div>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>
    </div>

    <!-- Products table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-packages me-1"></i>Products
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td>
                <div class="fw-medium">{{ row.item_name }}</div>
                <span class="rp-sub">{{ row.item_code }}<template v-if="row.sku && row.variant"> · {{ row.sku }}</template></span>
                <span v-for="part in variantParts(row)" :key="part" class="rp-variant mt-1">{{ part }}</span>
              </td>
              <td>
                {{ row.category || '—' }}
                <span v-if="row.sub_category" class="rp-sub">{{ row.sub_category }}</span>
              </td>
              <td class="text-end fw-semibold">
                {{ formatQty(row.qty_sold) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
                <span class="rp-sub">{{ plural(row.invoice_count, 'invoice') }}</span>
              </td>
              <td class="text-end" :class="Number(row.returned_qty) ? 'text-danger' : 'text-muted'">
                {{ Number(row.returned_qty) ? formatQty(row.returned_qty) : '—' }}
              </td>
              <td class="text-end">
                {{ formatMoney(row.avg_price) }}
                <span v-if="Number(row.discount)" class="rp-sub">{{ row.discount_rate }}% disc.</span>
              </td>
              <td class="text-end fw-semibold">{{ formatMoney(row.revenue) }}</td>
              <td class="text-end">{{ formatMoney(row.cost) }}</td>
              <td class="text-end fw-semibold" :class="netClass(row.profit)">{{ formatMoney(row.profit) }}</td>
              <td class="text-end">
                <span class="rp-badge" :class="marginTone(row.margin)">{{ row.margin }}%</span>
              </td>
              <td class="text-end sp-share">
                {{ row.share }}%
                <div class="rp-bar-track mt-1"><div class="rp-bar-fill" :style="{ width: `${Math.min(100, Number(row.share))}%` }"></div></div>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-packages"></i>
                  <div class="fw-semibold">No products sold</div>
                  <small class="text-muted">Try another period, customer or category.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th colspan="2" class="text-end">Total ({{ plural(rows.length, 'product') }})</th>
              <th class="text-end">{{ formatQty(summary.qty_sold) }}</th>
              <th class="text-end text-danger">{{ formatQty(summary.returned_qty) }}</th>
              <th></th>
              <th class="text-end">{{ formatMoney(summary.revenue) }}</th>
              <th class="text-end">{{ formatMoney(summary.cost) }}</th>
              <th class="text-end" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</th>
              <th class="text-end">{{ summary.margin }}%</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Revenue = sales after discount, before VAT, less what was returned in the period. Cost is the batch cost of the
      units sold, less the units put back. Avg price is before returns.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PROFIT_COLOR, SALES_COLOR, marginTone } from './salesReportMixin';

const EMPTY_SUMMARY = {
  qty_sold: 0, returned_qty: 0, qty: 0, gross: 0, discount: 0, vat: 0, sales_revenue: 0, returned_revenue: 0,
  revenue: 0, cost: 0, profit: 0, product_count: 0, invoice_count: 0, margin: 0, discount_rate: 0, return_rate: 0,
  top_products: [], by_category: [], best_seller: null, most_profitable: null, lowest_margin: null,
};

export default {
  name: 'SalesByProductReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_by_product_report.get,
  fileName: 'sales_by_product',
  columns: [
    { key: 'item_name', label: 'Product' },
    { key: 'category', label: 'Category' },
    { key: 'qty_sold', label: 'Sold', class: 'text-end', numeric: true },
    { key: 'returned_qty', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'avg_price', label: 'Avg Price', class: 'text-end', numeric: true },
    { key: 'revenue', label: 'Revenue', class: 'text-end', numeric: true },
    { key: 'cost', label: 'Cost', class: 'text-end', numeric: true },
    { key: 'profit', label: 'Gross Profit', class: 'text-end', numeric: true },
    { key: 'margin', label: 'Margin', class: 'text-end', numeric: true },
    { key: 'share', label: 'Share', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      SALES_COLOR,
      PROFIT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.customer;
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'revenue');
    },

    chartHeight() {
      return Math.max(170, this.summary.top_products.length * 26 + 30);
    },

    highlights() {
      const s = this.summary;
      return [
        s.best_seller && {
          label: 'Best seller', icon: 'ti-flame', tone: 'tone-orange', name: s.best_seller.name,
          detail: `${this.formatQty(s.best_seller.qty)} sold (net) · ${this.formatMoney(s.best_seller.revenue)}`,
        },
        s.most_profitable && {
          label: 'Most profitable', icon: 'ti-trophy', tone: 'tone-green', name: s.most_profitable.name,
          detail: `${this.formatMoney(s.most_profitable.value)} profit`,
        },
        s.lowest_margin && s.product_count > 1 && {
          label: 'Lowest margin', icon: 'ti-arrow-down-right', tone: 'tone-red', name: s.lowest_margin.name,
          detail: `${s.lowest_margin.value}% on ${this.formatMoney(s.lowest_margin.revenue)}`,
        },
      ].filter(Boolean);
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  methods: {
    marginTone,

    extraParams() {
      return { ...this.periodParams(), ...this.customerParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const top = this.summary.top_products;
      this.$nextTick(() => this.drawBars('top', this.$refs.topChart, top.map((p) => p.name), [
        { label: 'Revenue', data: top.map((p) => p.revenue), color: SALES_COLOR },
        { label: 'Profit', data: top.map((p) => p.profit), color: PROFIT_COLOR },
      ], { horizontal: true }));
    },

    variantParts(row) {
      return row.variant ? row.variant.split(' · ') : [];
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)} · ${escapeHtml(r.item_name)}${r.variant ? `<br><small>${escapeHtml(r.variant)}</small>` : ''}</td>
          <td>${escapeHtml(r.category || '—')}</td>
          <td class="num">${this.formatQty(r.qty_sold)}</td>
          <td class="num">${this.formatQty(r.returned_qty)}</td>
          <td class="num">${this.formatMoney(r.avg_price)}</td>
          <td class="num">${this.formatMoney(r.revenue)}</td>
          <td class="num">${this.formatMoney(r.cost)}</td>
          <td class="num">${this.formatMoney(r.profit)}</td>
          <td class="num">${r.margin}%</td>
          <td class="num">${r.share}%</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Product</th><th>Category</th><th class="num">Sold</th>
          <th class="num">Returned</th><th class="num">Avg Price</th><th class="num">Revenue</th><th class="num">Cost</th>
          <th class="num">Gross Profit</th><th class="num">Margin</th><th class="num">Share</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="2" class="num">Total</td><td class="num">${this.formatQty(s.qty_sold)}</td>
          <td class="num">${this.formatQty(s.returned_qty)}</td><td></td>
          <td class="num">${this.formatMoney(s.revenue)}</td><td class="num">${this.formatMoney(s.cost)}</td>
          <td class="num">${this.formatMoney(s.profit)}</td><td class="num">${s.margin}%</td><td></td></tr></tfoot></table>`;
      this.openPrint(
        'Sales by Product',
        [
          [this.periodText(), this.customerText(), ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Revenue', this.formatMoney(s.revenue)],
          ['Quantity Sold', `${this.formatQty(s.qty_sold)} · ${this.formatQty(s.returned_qty)} returned`],
          ['Cost', this.formatMoney(s.cost)],
          ['Gross Profit', `${this.formatMoney(s.profit)} (${s.margin}%)`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.sp-share {
  min-width: 90px;
}

.sp-share .rp-bar-track {
  height: 5px;
}
</style>
